import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const COLORS = [
  { id: 'default', value: '', bg: 'bg-white', border: 'border-slate-200' },
  { id: 'blue', value: '#3b82f6', bg: 'bg-blue-50', border: 'border-blue-500' },
  { id: 'indigo', value: '#6366f1', bg: 'bg-indigo-50', border: 'border-indigo-500' },
  { id: 'purple', value: '#a855f7', bg: 'bg-purple-50', border: 'border-purple-500' },
  { id: 'pink', value: '#ec4899', bg: 'bg-pink-50', border: 'border-pink-500' },
  { id: 'red', value: '#ef4444', bg: 'bg-red-50', border: 'border-red-500' },
  { id: 'orange', value: '#f97316', bg: 'bg-orange-50', border: 'border-orange-500' },
  { id: 'amber', value: '#f59e0b', bg: 'bg-amber-50', border: 'border-amber-500' },
  { id: 'green', value: '#22c55e', bg: 'bg-green-50', border: 'border-green-500' },
  { id: 'teal', value: '#14b8a6', bg: 'bg-teal-50', border: 'border-teal-500' },
  { id: 'cyan', value: '#06b6d4', bg: 'bg-cyan-50', border: 'border-cyan-500' },
  { id: 'slate', value: '#64748b', bg: 'bg-slate-50', border: 'border-slate-500' },
];

const ICONS = [
  'FiLayout', 'FiFile', 'FiFolder', 'FiBox', 'FiTag', 
  'FiUsers', 'FiUser', 'FiSettings', 'FiDatabase', 'FiServer',
  'FiCloud', 'FiGlobe', 'FiShoppingCart', 'FiCreditCard', 'FiDollarSign',
  'FiActivity', 'FiTrendingUp', 'FiPieChart', 'FiBarChart2', 'FiAward',
  'FiGift', 'FiHeart', 'FiStar', 'FiCheckCircle', 'FiAlertCircle',
  'FiHelpCircle', 'FiInfo', 'FiMail', 'FiMessageSquare', 'FiPhone',
  'FiCalendar', 'FiClock', 'FiMap', 'FiMapPin', 'FiNavigation',
  'FiImage', 'FiVideo', 'FiMusic', 'FiCamera', 'FiPrinter',
  'FiLock', 'FiUnlock', 'FiKey', 'FiShield', 'FiCpu',
  'FiSmartphone', 'FiMonitor', 'FiTablet', 'FiTv', 'FiWatch'
];

const NodeCustomizer = ({ isOpen, node, onClose, onUpdate, onDelete }) => {
  const [label, setLabel] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedIcon, setSelectedIcon] = useState('');
  const [iconSearch, setIconSearch] = useState('');
  const [showIconPicker, setShowIconPicker] = useState(false);

  useEffect(() => {
    if (node) {
      setLabel(node.label || '');
      setSelectedColor(node.color || '');
      setSelectedIcon(node.icon || '');
    }
  }, [node]);

  const handleSave = () => {
    let cleanLabel = label;
    if (cleanLabel === '<p><br></p>') cleanLabel = '';
    
    onUpdate(node.id, { 
      label: cleanLabel,
      color: selectedColor,
      icon: selectedIcon
    });
    onClose();
  };

  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      ['clean']
    ],
    clipboard: {
      matchVisual: false
    }
  };

  const filteredIcons = ICONS.filter(icon => 
    icon.toLowerCase().includes(iconSearch.toLowerCase())
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-900/20 backdrop-blur-[2px]"
            onClick={onClose}
          />
          <motion.div 
            initial={{ opacity: 0, scale: 0.9, y: 10 }} 
            animate={{ opacity: 1, scale: 1, y: 0 }} 
            exit={{ opacity: 0, scale: 0.9, y: 10 }}
            className="relative bg-white rounded-2xl shadow-2xl border border-white/50 w-full max-w-sm overflow-hidden flex flex-col max-h-[90vh]"
          >
            {/* Header */}
            <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center shrink-0">
              <h3 className="font-bold text-slate-700 flex items-center gap-2">
                <SafeIcon icon={FiIcons.FiSettings} className="text-indigo-500" />
                Customize Node
              </h3>
              <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded-full transition-colors text-slate-400">
                <SafeIcon icon={FiIcons.FiX} />
              </button>
            </div>

            <div className="p-5 space-y-5 overflow-y-auto">
              {/* Title Input */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Page Title</label>
                <div className="node-quill-wrapper">
                  <ReactQuill 
                    theme="snow"
                    value={label}
                    onChange={setLabel}
                    modules={modules}
                    className="bg-slate-50 rounded-xl overflow-hidden border border-slate-200 focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 transition-all"
                  />
                </div>
                <style>{`
                  .node-quill-wrapper .quill { border: none; }
                  .node-quill-wrapper .ql-toolbar { border: none; border-bottom: 1px solid #e2e8f0; background: #f8fafc; padding: 4px; }
                  .node-quill-wrapper .ql-container { border: none; font-size: 14px; }
                  .node-quill-wrapper .ql-editor { min-height: 50px; padding: 8px 12px; line-height: 1.4; }
                  .node-quill-wrapper .ql-formats { margin-right: 8px; }
                `}</style>
              </div>

              {/* Icon Picker */}
              <div className="space-y-2">
                 <label className="text-xs font-bold text-slate-400 uppercase tracking-wider flex justify-between items-center">
                    Icon
                    {selectedIcon && (
                        <button 
                            onClick={() => setSelectedIcon('')} 
                            className="text-[10px] text-red-500 hover:text-red-700 font-normal"
                        >
                            Reset to Default
                        </button>
                    )}
                 </label>
                 <div className="relative">
                    <button 
                        onClick={() => setShowIconPicker(!showIconPicker)}
                        className="w-full flex items-center gap-3 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg hover:border-indigo-300 transition-colors text-left"
                    >
                        <div className="w-8 h-8 rounded-md bg-white border border-slate-200 flex items-center justify-center text-slate-600">
                            <SafeIcon icon={selectedIcon ? FiIcons[selectedIcon] : FiIcons.FiBox} />
                        </div>
                        <div className="flex-1">
                            <span className="text-sm font-medium text-slate-700">{selectedIcon || 'Default Icon'}</span>
                            <p className="text-[10px] text-slate-400">Click to change icon</p>
                        </div>
                        <SafeIcon icon={FiIcons.FiChevronDown} className={`text-slate-400 transition-transform ${showIconPicker ? 'rotate-180' : ''}`} />
                    </button>

                    <AnimatePresence>
                        {showIconPicker && (
                            <motion.div 
                                initial={{ opacity: 0, y: 5 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 5 }}
                                className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-100 z-50 p-3"
                            >
                                <input 
                                    type="text" 
                                    placeholder="Search icons..." 
                                    value={iconSearch}
                                    onChange={(e) => setIconSearch(e.target.value)}
                                    className="w-full px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-md text-sm mb-2 focus:outline-none focus:border-indigo-500"
                                    autoFocus
                                />
                                <div className="grid grid-cols-6 gap-1 max-h-[160px] overflow-y-auto custom-scrollbar">
                                    {filteredIcons.map(iconName => (
                                        <button
                                            key={iconName}
                                            onClick={() => { setSelectedIcon(iconName); setShowIconPicker(false); }}
                                            className={`aspect-square rounded-md flex items-center justify-center text-lg hover:bg-slate-100 transition-colors ${selectedIcon === iconName ? 'bg-indigo-50 text-indigo-600 ring-1 ring-indigo-500' : 'text-slate-500'}`}
                                            title={iconName}
                                        >
                                            <SafeIcon icon={FiIcons[iconName]} />
                                        </button>
                                    ))}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                 </div>
              </div>

              {/* Color Picker */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Color Code</label>
                <div className="grid grid-cols-6 gap-2">
                  {COLORS.map((color) => (
                    <button
                      key={color.id}
                      onClick={() => setSelectedColor(color.value)}
                      className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-transform hover:scale-110 ${
                        selectedColor === color.value 
                          ? 'border-slate-600 ring-2 ring-slate-200 shadow-md scale-110' 
                          : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color.value || '#f1f5f9' }}
                      title={color.id}
                    >
                      {selectedColor === color.value && (
                        <SafeIcon icon={FiIcons.FiCheck} className={`text-xs ${!color.value ? 'text-slate-400' : 'text-white'}`} />
                      )}
                      {!color.value && selectedColor !== '' && <span className="text-slate-300 text-[10px]">x</span>}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex justify-between items-center shrink-0">
              {node?.type !== 'root' ? (
                <button 
                  onClick={() => {
                    if(confirm("Delete this node and all its children?")) {
                      onDelete(node.id);
                      onClose();
                    }
                  }}
                  className="text-red-500 hover:text-red-700 text-xs font-bold uppercase tracking-wide px-2 py-1 hover:bg-red-50 rounded transition-colors"
                >
                  Delete
                </button>
              ) : <div></div>}
              
              <button 
                onClick={handleSave}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium shadow-lg shadow-indigo-200 transition-all active:scale-95"
              >
                Save Changes
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default NodeCustomizer;